import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Modal } from "react-responsive-modal";
import { LightIcon } from "../../../AssetComponents/Images";
import { PurpleButton } from "../../../Components/Buttons/PurpleButton";
import { GreenButton } from "../../../Components/Buttons/GreenButton";
import { RedButton } from "../../../Components/Buttons/RedButton";
import { toast } from "react-toastify";
import { executePerps, queryContract, getQueryableAddress } from "../../functions/Contract";
import { useDispatch, useSelector } from "react-redux";
import { useWalletConnection } from "../../../hooks/useWalletConnection";
import { formatPriceWithSignificantDecimals } from "../../../Components/utils/formatter";
import { useMarketPrice } from "../../../hooks/useMarketPrices";
import { getMarketByMarketIndex } from "../../../config/markets";
import {
  showTransactionToast,
  updateTransactionToast,
  dismissTransactionToast,
  showTxSuccess,
  showTxError,
} from "../../Toast/TransactionToast";
// Import EVM trading services
import {
  closeTradeMarketEvm,
  updateTakeProfitEvm,
  updateStopLossEvm,
  updateLeverageEvm,
  executeSimpleFunction,
  updateLimitOrderEvm,
  cancelLimitOrderEvm,
  increasePositionSizeEvm,
  decreasePositionSizeEvm,
  triggerTradeEvm,
  getCurrentUserBech32Address
} from "../../../evm/services/tradingEvmService";
import { getNibiruSDK, getContractAddresses } from "../../../config/network";

const nibiru = getNibiruSDK();

// Low-price token IDs that need special formatting
const lowPriceTokenIds = new Set(["pump", "pengu", "bonk", "shib", "nibi"]);

// Format price for low-value tokens using CoinGecko-style notation
const formatLowTokenPrice = (priceValue, isLowPriceToken) => {
  if (!priceValue && priceValue !== 0) return "0.00";

  const num = parseFloat(priceValue);
  if (isNaN(num)) return "0.00";

  const abs = Math.abs(num);

  // For low-price tokens < 1, show 0.0ₙddd style (CoinGecko format)
  if (isLowPriceToken && abs > 0 && abs < 1) {
    const raw = abs.toFixed(20);
    const decimalPart = raw.split(".")[1] || "";

    let zeroCount = 0;
    for (let i = 0; i < decimalPart.length; i++) {
      if (decimalPart[i] === "0") {
        zeroCount += 1;
      } else {
        break;
      }
    }

    const remaining = decimalPart.slice(zeroCount).slice(0, 3);

    if (!remaining) {
      return num.toFixed(8);
    }

    return (
      <>
        0.0<sub>{zeroCount}</sub>{remaining}
      </>
    );
  }

  // For low-price tokens >= 1, show 6 decimals
  if (isLowPriceToken && abs >= 1) {
    return num.toFixed(6);
  }

  // Fallback to standard formatter
  return formatPriceWithSignificantDecimals(num, 8, false);
};

// All available tabs (constant, defined outside component for optimization)
const ALL_TABS = [
  { id: "position", label: "Position" },
  { id: "limit", label: "Limit Order" },
  { id: "actions", label: "Actions" },
];

const formatNumber = (val) => {
  if (!val && val !== 0) return "";
  // Strip commas before parsing to avoid parseFloat("3,019.76") returning 3
  const cleanVal = String(val).replace(/,/g, "");
  const num = parseFloat(cleanVal);
  if (isNaN(num)) return "";
  
  // Use enhanced formatting for small values
  return formatPriceWithSignificantDecimals(num, 8, false);
};

const unformatNumber = (val) => {
  if (!val) return "";
  return val.toString().replace(/,/g, "");
};

// Add toast deduplication helper
const useToastDeduplication = () => {
  const lastToastRef = useRef({ message: '', timestamp: 0 });
  
  const showToast = (type, message) => {
    const now = Date.now();
    const timeSinceLastToast = now - lastToastRef.current.timestamp;
    
    // Only show toast if message is different or enough time has passed (3 seconds)
    if (lastToastRef.current.message !== message || timeSinceLastToast > 3000) {
      toast[type](message);
      lastToastRef.current = { message, timestamp: now };
    }
  };
  
  return { showToast };
};

const UpdateActionsModal = ({
  isOpen,
  setIsOpen,
  trade,
  tp,
  setTp,
  sl,
  setSl,
  leverage_value,
  setLeverageValue,
  selectedTradeIndex,
}) => {
  const dispatch = useDispatch();
  const { account, walletType, isEvm, isCosmos } = useWalletConnection();
  const { showToast } = useToastDeduplication();
  
  // State for liquidation price from contract
  const [liquidationPrice, setLiquidationPrice] = useState(null);
  const [loadingLiquidationPrice, setLoadingLiquidationPrice] = useState(false);
  
  // Get market-specific price using the hook (same as TradesRow)
  // This ensures we get the correct price for the trade's market, not the global Redux price
  const { price: marketSpecificPrice } = useMarketPrice(
    trade?.market_index ? trade.market_index : null,
    30000 // Update every 30 seconds
  );
  
  // Fallback to Redux global price if market-specific price is not available
  const globalMarketPrice = useSelector((state) => state.trade.currentPrice);
  const currentMarketPrice = marketSpecificPrice !== null && marketSpecificPrice !== undefined 
    ? marketSpecificPrice 
    : globalMarketPrice;

  // Determine if this is a low-price token
  const market = trade?.market_index ? getMarketByMarketIndex(trade.market_index) : null;
  const isLowPriceToken = market && lowPriceTokenIds.has(market.id);

  // Helper function to format prices as string (for toasts and template strings)
  const formatPriceString = (priceValue, includeCurrency = true) => {
    if (isLowPriceToken) {
      const num = parseFloat(priceValue);
      if (isNaN(num)) return includeCurrency ? "$0.00" : "0.00";
      
      const abs = Math.abs(num);
      if (abs > 0 && abs < 1) {
        const raw = abs.toFixed(20);
        const decimalPart = raw.split(".")[1] || "";
        let zeroCount = 0;
        for (let i = 0; i < decimalPart.length; i++) {
          if (decimalPart[i] === "0") {
            zeroCount += 1;
          } else {
            break;
          }
        }
        const remaining = decimalPart.slice(zeroCount).slice(0, 3);
        if (!remaining) {
          const formatted = num.toFixed(8);
          return includeCurrency ? `$${formatted}` : formatted;
        }
        // Convert subscript notation to string: 0.0₂55 -> 0.00255
        const formatted = `0.${'0'.repeat(zeroCount)}${remaining}`;
        return includeCurrency ? `$${formatted}` : formatted;
      }
      if (abs >= 1) {
        const formatted = num.toFixed(6);
        return includeCurrency ? `$${formatted}` : formatted;
      }
    }
    return formatPriceWithSignificantDecimals(priceValue, 8, includeCurrency);
  };

  // Helper function to format prices as JSX (for display in JSX contexts)
  const formatPriceJSX = (priceValue, includeCurrency = true) => {
    if (isLowPriceToken) {
      const formatted = formatLowTokenPrice(priceValue, isLowPriceToken);
      if (React.isValidElement(formatted)) {
        // It's JSX with subscript (e.g., 0.0₂55)
        return includeCurrency ? (
          <>
            ${formatted}
          </>
        ) : formatted;
      }
      // It's a string
      return includeCurrency ? `$${formatted}` : formatted;
    }
    const formatted = formatPriceWithSignificantDecimals(priceValue, 8, false);
    return includeCurrency ? `$${formatted}` : formatted;
  };

  // Alias for backward compatibility (use string version for toasts)
  const formatPrice = formatPriceString;

  // Helper function to format numbers for input display (handles low-price tokens)
  // This preserves the original precision of the input string when possible
  const formatNumberForInput = (val) => {
    if (!val && val !== 0) return "";
    
    // Convert to string and remove commas
    const valStr = String(val).replace(/,/g, "").trim();
    
    // If empty, return empty
    if (valStr === '') return "";
    
    // For low-price tokens, try to preserve the original string format
    if (isLowPriceToken) {
      // Check if it's a valid decimal number string
      if (/^-?\d*\.?\d*$/.test(valStr)) {
        const num = parseFloat(valStr);
        if (isNaN(num)) return "";
        
        // For very small values (< 1), preserve original decimals if reasonable
        if (num > 0 && num < 1 && valStr.includes('.')) {
          const decimalPart = valStr.split('.')[1] || '';
          // If original has 8 or fewer decimals, preserve it
          if (decimalPart.length <= 8) {
            return valStr;
          }
          // Otherwise format with 8 decimals
          return num.toFixed(8);
        }
        
        // For values >= 1, format with 6 decimals
        if (num >= 1) {
          return num.toFixed(6);
        }
        
        // For zero or negative, return as is
        return valStr;
      }
    }
    
    // For regular tokens, use standard formatting but preserve precision
    const num = parseFloat(valStr);
    if (isNaN(num)) return "";
    return formatPriceWithSignificantDecimals(num, 8, false);
  };
  
  // Optimized: Filter tabs based on trade type using useMemo
  const tabs = useMemo(() => {
    const isLimitOrder = trade?.trade_type === "limit";
    if (isLimitOrder) {
      // For limit orders: show all tabs (Position, Limit Order, Actions)
      return ALL_TABS;
    } else {
      // For non-limit orders: show only Position and Actions
      return ALL_TABS.filter(tab => tab.id !== "limit");
    }
  }, [trade?.trade_type]);
  
  const PendingOrderType = {
    LIMIT_OPEN: "limit_open",
    STOP_OPEN: "stop_open",
    TP_CLOSE: "tp_close",
    SL_CLOSE: "sl_close",
    LIQ_CLOSE: "liq_close",
  };
  
  const [activeTab, setActiveTab] = useState("position");
  
  // Ensure activeTab is valid when tabs change
  useEffect(() => {
    const isValidTab = tabs.some(tab => tab.id === activeTab);
    if (!isValidTab && tabs.length > 0) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);
  const [modalInputs, setModalInputs] = useState({
    price: trade.open_price,
    newTp: trade.tp,
    newSl: trade.sl,
    leverage: trade.leverage,
    slippage_p: "0.5",
    leverageDelta: "",
    collateralDelta: "",
    orderType: "limit_open",
  });
  
  // LOCAL focused field tracker
  const [focusedField, setFocusedField] = useState("");

  // 🔥 GET TRADE INDEX NUMBER HELPER
  const getTradeIndexNumber = (tradeIndex) => {
    if (typeof tradeIndex === 'string' && tradeIndex.includes('UserTradeIndex')) {
      const match = tradeIndex.match(/UserTradeIndex\((\d+)\)/);
      return match ? parseInt(match[1]) : parseInt(tradeIndex);
    }
    return parseInt(tradeIndex);
  };

  // Fetch liquidation price from contract
  const fetchLiquidationPrice = useCallback(async () => {
    if (!account || !trade || !trade.user_trade_index) {
      return;
    }

    try {
      setLoadingLiquidationPrice(true);
      
      // Get queryable address (converts EVM to bech32 if needed)
      const queryAddress = await getQueryableAddress(account, walletType);
      
      if (!queryAddress) {
        console.error("Failed to get valid query address for liquidation price");
        setLoadingLiquidationPrice(false);
        return;
      }

      const tradeIndexNumber = getTradeIndexNumber(trade.user_trade_index);
      
      const message = {
        get_trade: {
          trader: queryAddress,
          index: tradeIndexNumber
        }
      };

      const result = await queryContract(
        getContractAddresses().perp,
        message,
        nibiru.endptTm
      );

      // Handle different response formats
      let liquidationPriceValue = null;
      if (result && result.liquidation_price) {
        liquidationPriceValue = parseFloat(result.liquidation_price);
      } else if (result && result.data && result.data.liquidation_price) {
        liquidationPriceValue = parseFloat(result.data.liquidation_price);
      }

      if (liquidationPriceValue && !isNaN(liquidationPriceValue)) {
        setLiquidationPrice(liquidationPriceValue);
        console.log("Fetched liquidation price from contract:", liquidationPriceValue);
      } else {
        console.warn("No valid liquidation price in response:", result);
        setLiquidationPrice(null);
      }
    } catch (error) {
      console.error("Failed to fetch liquidation price:", error);
      setLiquidationPrice(null);
    } finally {
      setLoadingLiquidationPrice(false);
    }
  }, [account, walletType, trade]);

  // Fetch liquidation price when modal opens or trade changes
  useEffect(() => {
    if (isOpen && account && trade && trade.user_trade_index) {
      fetchLiquidationPrice();
    } else {
      setLiquidationPrice(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, account, trade?.user_trade_index, fetchLiquidationPrice]);

  // Update the leverage validation helper
  const validateLeverage = (leverageValue) => {
    const leverage = parseFloat(leverageValue);
    
    if (isNaN(leverage)) {
      showToast('error', 'Please enter a valid leverage value');
      return false;
    }
    
    // Check if it's a whole number (no decimals)
    if (leverage % 1 !== 0) {
      showToast('error', 'Leverage must be a whole number (no decimals)');
      return false;
    }
    
    if (leverage < 1) {
      showToast('error', 'Minimum leverage is 1x');
      return false;
    }
    
    if (leverage > 100) {
      showToast('error', 'Maximum leverage is 100x');
      return false;
    }
    
    return true;
  };

  // --- Shared TP/SL validation helpers --------------------------------------
  // These ensure we never send a tx that violates the on-screen TP/SL rules.

  const validateTPConditions = (tpValueRaw) => {
    if (!tpValueRaw && tpValueRaw !== 0) return true; // nothing to validate

    const tpValue = parseFloat(tpValueRaw);
    const market = parseFloat(currentMarketPrice);
    const entry = parseFloat(trade?.open_price);

    if (isNaN(tpValue)) {
      showToast("error", "Please enter a valid Take Profit price.");
      return false;
    }

    // LONG: TP must be above BOTH market and entry
    if (trade.long) {
      if (!isNaN(market) && tpValue <= market) {
        showToast(
          "error",
          `Take profit must be above the market price (${formatPrice(market, true)}) for a long position.`
        );
        return false;
      }
      if (!isNaN(entry) && tpValue <= entry) {
        showToast(
          "error",
          `Take profit must be above your entry price (${formatPrice(entry, true)}) for a long position.`
        );
        return false;
      }
    } else {
      // SHORT: TP must be below BOTH market and entry
      if (!isNaN(market) && tpValue >= market) {
        showToast(
          "error",
          `Take profit must be below the market price (${formatPrice(market, true)}) for a short position.`
        );
        return false;
      }
      if (!isNaN(entry) && tpValue >= entry) {
        showToast(
          "error",
          `Take profit must be below your entry price (${formatPrice(entry, true)}) for a short position.`
        );
        return false;
      }
    }

    return true;
  };

  const validateSLConditions = (slValueRaw) => {
    if (!slValueRaw && slValueRaw !== 0) return true; // nothing to validate

    const slValue = parseFloat(slValueRaw);
    const market = parseFloat(currentMarketPrice);
    const entry = parseFloat(trade?.open_price);

    if (isNaN(slValue)) {
      showToast("error", "Please enter a valid Stop Loss price.");
      return false;
    }

    // If we have a liquidation price, enforce "between market/entry and liq"
    const hasLiq = liquidationPrice !== null && liquidationPrice > 0;

    if (trade.long) {
      // LONG: SL must be BELOW both market and entry, and ABOVE liquidation (if known)
      if (!isNaN(market) && slValue >= market) {
        toast.error(
          `Stop Loss must be below the market price (${formatPrice(market, true)}) for a long position.`,
          {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
        return false;
      }
      if (!isNaN(entry) && slValue >= entry) {
        toast.error(
          `Stop Loss must be below your entry price (${formatPrice(entry, true)}) for a long position.`,
          {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
        return false;
      }
      if (hasLiq && slValue <= liquidationPrice) {
        toast.error(
          `Stop Loss must be above liquidation price (${formatPrice(liquidationPrice, true)}) for a long position.`,
          {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
        return false;
      }
    } else {
      // SHORT: SL must be ABOVE both market and entry, and BELOW liquidation (if known)
      if (!isNaN(market) && slValue <= market) {
        toast.error(
          `Stop Loss must be above the market price (${formatPrice(market, true)}) for a short position.`,
          {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
        return false;
      }
      if (!isNaN(entry) && slValue <= entry) {
        toast.error(
          `Stop Loss must be above your entry price (${formatPrice(entry, true)}) for a short position.`,
          {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
        return false;
      }
      if (hasLiq && slValue >= liquidationPrice) {
        toast.error(
          `Stop Loss must be below liquidation price (${formatPrice(liquidationPrice, true)}) for a short position.`,
          {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
        return false;
      }
    }

    return true;
  };

  // --- UI helpers: compute simple guidance ranges for TP / SL ---------------
  // These are only for display (chips/pills), validation is handled separately.

  const getTpGuidance = () => {
    const market = parseFloat(currentMarketPrice);
    const entry = parseFloat(trade?.open_price);

    const hasMarket = !Number.isNaN(market);
    const hasEntry = !Number.isNaN(entry);
    if (!hasMarket && !hasEntry) return null;

    if (trade.long) {
      const minTp = Math.max(
        hasMarket ? market : -Infinity,
        hasEntry ? entry : -Infinity
      );
      if (!Number.isFinite(minTp)) return null;
      return { type: "min", value: minTp };
    } else {
      const maxTp = Math.min(
        hasMarket ? market : Infinity,
        hasEntry ? entry : Infinity
      );
      if (!Number.isFinite(maxTp)) return null;
      return { type: "max", value: maxTp };
    }
  };

  const getSlGuidance = () => {
    const market = parseFloat(currentMarketPrice);
    const entry = parseFloat(trade?.open_price);
    const liq = liquidationPrice;

    const hasMarket = !Number.isNaN(market);
    const hasEntry = !Number.isNaN(entry);
    const hasLiq = liq !== null && liq > 0;

    if (!hasMarket && !hasEntry && !hasLiq) return null;

    if (trade.long) {
      // Long: SL between liquidation and min(market, entry)
      const upper = Math.min(
        hasMarket ? market : Infinity,
        hasEntry ? entry : Infinity
      );
      const lower = hasLiq ? liq : null;
      return { lower, upper, hasLiq: !!hasLiq };
    } else {
      // Short: SL between max(market, entry) and liquidation
      const lower = Math.max(
        hasMarket ? market : -Infinity,
        hasEntry ? entry : -Infinity
      );
      const upper = hasLiq ? liq : null;
      return { lower, upper, hasLiq: !!hasLiq };
    }
  };

  // Enhanced leverage input handler with decimal prevention
  const handleLeverageChange = (value, field = 'leverage') => {
    // Remove any non-numeric characters except for temporary input states
    const cleanValue = value.replace(/[^0-9]/g, '');
    
    // Allow empty input for user to type
    if (cleanValue === '') {
      setModalInputs({ ...modalInputs, [field]: '' });
      return;
    }
    
    const parsed = parseInt(cleanValue, 10);
    
    // Prevent input of values over 100
    if (parsed > 100) {
      showToast('error', 'Maximum leverage is 100x');
      return; // Don't update state if over limit
    }
    
    // Prevent input of 0
    if (parsed === 0) {
      showToast('error', 'Minimum leverage is 1x');
      return;
    }
    
    setModalInputs({ ...modalInputs, [field]: cleanValue });
  };

  const handleUpdateLimitOrder = async () => {
    try {
      if (!account) {
        showToast('error', "Please connect your wallet");
        return;
      }

      if (isEvm) {
        const tradeIndexNumber = getTradeIndexNumber(trade.user_trade_index);
        const result = await updateLimitOrderEvm(
          tradeIndexNumber,
          unformatNumber(modalInputs.price),
          modalInputs.newTp ? unformatNumber(modalInputs.newTp) : null,
          modalInputs.newSl ? unformatNumber(modalInputs.newSl) : null,
          unformatNumber(modalInputs.slippage_p),
          dispatch
        );
        
        if (!result.success) {
          throw new Error(result.error || "EVM update limit order failed");
        }
        
        showToast('success', "Limit order updated successfully");
        return result;
      } else {
        // 🌌 COSMOS WALLET - Existing implementation
        const message = {
          update_open_limit_order: {
            trade_index: trade.user_trade_index,
            price: unformatNumber(modalInputs.price),
            tp: modalInputs.newTp ? unformatNumber(modalInputs.newTp) : undefined,
            sl: modalInputs.newSl ? unformatNumber(modalInputs.newSl) : undefined,
            slippage_p: unformatNumber(modalInputs.slippage_p),
          },
        };

        const res = await executePerps(
          nibiru.chainId,
          nibiru.endptTm,
          getContractAddresses().perp,
          dispatch,
          message,
          account,
          walletType
        );

        if (!res) {
          throw new Error("Transaction failed");
        }
        showToast('success', "Limit order updated successfully");
        return res;
      }
    } catch (err) {
      console.error("Update limit order error:", err);
      showToast('error', `Failed to update limit order: ${err.message}`);
      return false;
    }
  };

  const handleCancelLimitOrder = async () => {
    try {
      if (!account) {
        showToast('error', "Please connect your wallet");
        return;
      }

      if (isEvm) {
        // 🔥 EVM WALLET - Use new EVM service
        const tradeIndexNumber = getTradeIndexNumber(trade.user_trade_index);
        const result = await cancelLimitOrderEvm(tradeIndexNumber, dispatch);
        
        if (!result.success) {
          throw new Error(result.error || "EVM cancel limit order failed");
        }
        
        showToast('success', "Limit order cancelled successfully");
        return result;
      } else {
        // 🌌 COSMOS WALLET - Existing implementation
        const message = {
          close_trade: {
            trade_index: trade.user_trade_index,
          },
        };

        const res = await executePerps(
          nibiru.chainId,
          nibiru.endptTm,
          getContractAddresses().perp,
          dispatch,
          message,
          account,
          walletType
        );

        if (!res) {
          throw new Error("Transaction failed");
        }
        showToast('success', "Limit order cancelled successfully");
        return res;
      }
    } catch (err) {
      console.error("Cancel limit order error:", err);
      showToast('error', `Failed to cancel limit order: ${err.message}`);
      return false;
    }
  };

  const handleUpdateTp = async () => {
    try {
      if (!account) {
        showToast('error', "Please connect your wallet");
        return false;
      }

      // Enforce on-screen TP conditions (relative to market price)
      if (!validateTPConditions(modalInputs.newTp)) {
        return false;
      }

      if (isEvm) {
        // �� EVM WALLET - Use EVM service
        const tradeIndexNumber = getTradeIndexNumber(trade.user_trade_index);
        const result = await updateTakeProfitEvm(tradeIndexNumber, modalInputs.newTp, dispatch);
        
        if (!result.success) {
          throw new Error(result.error || "EVM update take profit failed");
        }
        
        return result;
      } else {
        // 🌌 COSMOS WALLET - Existing implementation
        const message = {
          update_tp: {
            trade_index: trade.user_trade_index,
            new_tp: modalInputs.newTp,
            new_sl: null,
          },
        };

        const res = await executePerps(
          nibiru.chainId,
          nibiru.endptTm,
          getContractAddresses().perp,
          dispatch,
          message,
          account,
          walletType
        );

        if (!res) {
          throw new Error("Transaction failed");
        }
        showToast('success', "Take profit updated successfully");
        return res;
      }
    } catch (err) {
      console.error("Update TP error:", err);
      showToast('error', `Failed to update take profit: ${err.message}`);
      return false;
    }
  };

  const handleUpdateSl = async () => {
    try {
      if (!account) {
        showToast('error', "Please connect your wallet");
        return false;
      }

      // Enforce on-screen SL conditions (relative to market & liquidation)
      if (!validateSLConditions(modalInputs.newSl)) {
        return false;
      }

      if (isEvm) {
        // �� EVM WALLET - Use EVM service
        const tradeIndexNumber = getTradeIndexNumber(trade.user_trade_index);
        const result = await updateStopLossEvm(tradeIndexNumber, modalInputs.newSl, dispatch);
        
        if (!result.success) {
          throw new Error(result.error || "EVM update stop loss failed");
        }
        
        return result;
      } else {
        // 🌌 COSMOS WALLET - Existing implementation
        const message = {
          update_tp: {
            trade_index: trade.user_trade_index,
            new_tp: null,
            new_sl: modalInputs.newSl,
          },
        };

        const res = await executePerps(
          nibiru.chainId,
          nibiru.endptTm,
          getContractAddresses().perp,
          dispatch,
          message,
          account,
          walletType
        );

        if (!res) {
          throw new Error("Transaction failed");
        }
        showToast('success', "Stop loss updated successfully");
        return res;
      }
    } catch (err) {
      console.error("Update SL error:", err);
      showToast('error', `Failed to update stop loss: ${err.message}`);
      return false;
    }
  };

  const handleUpdateLeverage = async () => {
    try {
      if (!account) {
        showToast('error', "Please connect your wallet");
        return;
      }

      const currentLeverage = parseFloat(trade.leverage);
      const newLeverage = parseFloat(modalInputs.leverage);

      // Validate leverage
      if (!validateLeverage(modalInputs.leverage)) {
        return;
      }

      if (newLeverage === currentLeverage) {
        showToast('error', "Leverage must be different from the current leverage.");
        return;
      }

      if (isEvm) {
        // �� EVM WALLET - Use EVM service
        const tradeIndexNumber = getTradeIndexNumber(trade.user_trade_index);
        const result = await updateLeverageEvm(tradeIndexNumber, modalInputs.leverage, dispatch);
        
        if (!result.success) {
          throw new Error(result.error || "EVM update leverage failed");
        }
        
        return result;
      } else {
        // 🌌 COSMOS WALLET - Existing implementation
        const message = {
          update_leverage: {
            trade_index: trade.user_trade_index,
            new_leverage: modalInputs.leverage,
          },
        };

        const res = await executePerps(
          nibiru.chainId,
          nibiru.endptTm,
          getContractAddresses().perp,
          dispatch,
          message,
          account,
          walletType
        );

        if (!res) {
          throw new Error("Transaction failed");
        }
        showToast('success', "Leverage updated successfully");
        return res;
      }
    } catch (err) {
      console.error("Update leverage error:", err);
      showToast('error', `Failed to update leverage: ${err.message}`);
      return false;
    }
  };

  // COMMENTED OUT: Increase Position Size functionality
  /* const handleIncreasePositionSize = async () => {
    try {
      if (!account) {
        showToast('error', "Please connect your wallet");
        return;
      }

      if (isEvm) {
        const tradeIndexNumber = getTradeIndexNumber(trade.user_trade_index);
        const leverageDelta = modalInputs.leverageDelta ? unformatNumber(String(modalInputs.leverageDelta)) : "0";
        const price = modalInputs.price ? unformatNumber(String(modalInputs.price)) : "0";
        const slippage = modalInputs.slippage_p ? unformatNumber(String(modalInputs.slippage_p)) : "0.5";
        
        const result = await increasePositionSizeEvm(
          tradeIndexNumber,
          leverageDelta,
          price,
          slippage,
          dispatch
        );
        
        if (!result.success) {
          throw new Error(result.error || "EVM increase position size failed");
        }
        
        showToast('success', "Position size increased successfully");
        return result;
      } else {
        const leverageDelta = modalInputs.leverageDelta ? unformatNumber(String(modalInputs.leverageDelta)) : "0";
        const price = modalInputs.price ? unformatNumber(String(modalInputs.price)) : "0";
        const slippage = modalInputs.slippage_p ? unformatNumber(String(modalInputs.slippage_p)) : "0.5";
        
        const message = {
          increase_position_size: {
            trade_index: trade.user_trade_index,
            leverage_delta: leverageDelta,
            open_price: price,
            slippage_p: slippage,
          },
        };

        console.log("Sending increase position message:", message);
        
        const res = await executePerps(
          nibiru.chainId,
          nibiru.endptTm,
          getContractAddresses().perp,
          dispatch,
          message,
          account,
          walletType,
          null
        );

        if (!res) {
          throw new Error("Transaction failed");
        }
        showToast('success', "Position size increased successfully");
        return res;
      }
    } catch (err) {
      console.error("Increase position size error:", err);
      showToast('error', `Failed to increase position size: ${err.message}`);
      return false;
    }
  }; */
  
  // COMMENTED OUT: Decrease Position Size functionality
  /* const handleDecreasePositionSize = async () => {
    try {
      if (!account) {
        showToast('error', "Please connect your wallet");
        return;
      }

      if (isEvm) {
        const tradeIndexNumber = getTradeIndexNumber(trade.user_trade_index);
        const leverageDelta = modalInputs.leverageDelta ? unformatNumber(String(modalInputs.leverageDelta)) : "0";
        const collateralDelta = modalInputs.collateralDelta ? unformatNumber(String(modalInputs.collateralDelta)) : "0";
        
        const result = await decreasePositionSizeEvm(
          tradeIndexNumber,
          leverageDelta,
          collateralDelta,
          dispatch
        );
        
        if (!result.success) {
          throw new Error(result.error || "EVM decrease position size failed");
        }
        
        showToast('success', "Position size decreased successfully");
        return result;
      } else {
        const leverageDelta = modalInputs.leverageDelta ? unformatNumber(String(modalInputs.leverageDelta)) : "0";
        const collateralDelta = modalInputs.collateralDelta ? unformatNumber(String(modalInputs.collateralDelta)) : "0";
        
        const message = {
          decrease_position_size: {
            trade_index: trade.user_trade_index,
            leverage_delta: leverageDelta,
            collateral_delta: collateralDelta,
          },
        };

        console.log("Sending decrease position message:", message);
        
        const res = await executePerps(
          nibiru.chainId,
          nibiru.endptTm,
          getContractAddresses().perp,
          dispatch,
          message,
          account,
          walletType,
          null
        );

        if (!res) {
          throw new Error("Transaction failed");
        }
        showToast('success', "Position size decreased successfully");
        return res;
      }
    } catch (err) {
      console.error("Decrease position size error:", err);
      showToast('error', `Failed to decrease position size: ${err.message}`);
      return false;
    }
  }; */

  const handleTriggerTrade = async () => {
    try {
      if (!account) {
        showToast('error', "Please connect your wallet");
        return;
      }

      console.log("Trade details for triggering:", {
        trade_type: trade.trade_type,
        isOpen: trade.isOpen,
        user_trade_index: trade.user_trade_index,
        orderType: modalInputs.orderType
      });

      if (trade.isOpen === true) {
        showToast('error', "Cannot trigger an already opened trade. Use 'Position' tab to manage open trades.");
        return;
      }

      if (trade.trade_type === "trade") {
        showToast('error', "Cannot trigger market trades. Only limit/stop orders can be triggered.");
        return;
      }

      if (isEvm) {
        const tradeIndexNumber = getTradeIndexNumber(trade.user_trade_index);
        
        let traderAddress;
        try {
          traderAddress = await getCurrentUserBech32Address();
          console.log("✅ Got bech32 address for EVM wallet:", traderAddress);
        } catch (addressError) {
          console.error("Failed to get bech32 address:", addressError);
          showToast('error', "Failed to get wallet address. Please try again.");
          return;
        }

        const result = await triggerTradeEvm(
          tradeIndexNumber,
          traderAddress,  // Use bech32 address
          modalInputs.orderType,
          dispatch
        );
        
        if (!result.success) {
          throw new Error(result.error || "EVM trigger trade failed");
        }
        
        showToast('success', "Trade triggered successfully");
        return result;
      } else {
        // 🌌 COSMOS WALLET - Existing implementation
        const message = {
          trigger_trade: {
            trader: account, // This should be bech32 address for Cosmos wallets
            trade_index: trade.user_trade_index,
            order_type: modalInputs.orderType,
          },
        };

        console.log("Sending trigger trade message:", message);

        const res = await executePerps(
          nibiru.chainId,
          nibiru.endptTm,
          getContractAddresses().perp,
          dispatch,
          message,
          account,
          walletType
        );

        if (!res) {
          throw new Error("Transaction failed");
        }
        showToast('success', "Trade triggered successfully");
        return res;
      }
    } catch (err) {
      console.error("Trigger trade error:", err);
      showToast('error', `Failed to trigger trade: ${err.message}`);
      return false;
    }
  };

  const handleCloseTrade = async () => {
    try {
      if (!account) {
        showToast('error', "Please connect your wallet");
        return;
      }

      if (isEvm) {
        // �� EVM WALLET - Use EVM service
        const tradeIndexNumber = getTradeIndexNumber(trade.user_trade_index);
        const result = await closeTradeMarketEvm(tradeIndexNumber, true, dispatch);
        
        if (!result.success) {
          throw new Error(result.error || "EVM close trade failed");
        }
        
        return result;
      } else {
        // 🌌 COSMOS WALLET - Existing implementation
        const message = {
          close_trade: {
            trade_index: trade.user_trade_index,
          },
        };

        const res = await executePerps(
          nibiru.chainId,
          nibiru.endptTm,
          getContractAddresses().perp,
          dispatch,
          message,
          account,
          walletType
        );

        if (!res) {
          throw new Error("Transaction failed");
        }

        return res;
      }
    } catch (err) {
      console.error("Close trade error:", err);
      // showToast('error', `Failed to close trade: ${err.message}`);
      return false;
    }
  };

  const handleUpdatePosition = async () => {
    try {
      if (!account) {
        showToast('error', "Please connect your wallet");
        return false;
      }

      // Check if trade is still active
      if (trade.status === "closed") {
        showToast('error', "Cannot update a closed trade");
        return false;
      }

      // Get values from inputs
      const newTpValue = modalInputs.newTp && modalInputs.newTp.trim() !== "" ? modalInputs.newTp : null;
      const newSlValue = modalInputs.newSl && modalInputs.newSl.trim() !== "" ? modalInputs.newSl : null;
      const newLeverageValue = modalInputs.leverage && modalInputs.leverage.trim() !== "" ? modalInputs.leverage : null;
      
      // If no change in any value, show a message and return
      if (!newTpValue && !newSlValue && !newLeverageValue) {
        showToast('info', "No changes were made to the position");
        return false;
      }

      // Enforce shared TP/SL conditions against current market / liquidation
      if (newTpValue && !validateTPConditions(newTpValue)) {
        return false;
      }
      if (newSlValue && !validateSLConditions(newSlValue)) {
        return false;
      }
      
      // --- Validate leverage early (before any tx) ---
      let needsLeverageUpdate = false;
      if (newLeverageValue) {
        if (!validateLeverage(newLeverageValue)) {
          return false;
        }
        const currentLeverage = parseFloat(trade.leverage);
        const newLeverage = parseFloat(newLeverageValue);
        if (newLeverage === currentLeverage) {
          console.log("Leverage unchanged, skipping leverage update");
        } else if (newLeverage < currentLeverage) {
          showToast('error', "New leverage must be higher than current leverage");
          return false;
        } else {
          needsLeverageUpdate = true;
        }
      }

      // --- Validate TP/SL early (before any tx) ---
      let needsTpUpdate = false;
      let needsSlUpdate = false;
      if (newTpValue || newSlValue) {
        const entryPrice = parseFloat(trade.open_price);

        if (newTpValue) {
          const takeProfit = parseFloat(newTpValue);
          if (trade.long && takeProfit <= entryPrice) {
            showToast('error', "Take profit must be above the entry price for a long position.");
            return false;
          } else if (!trade.long && takeProfit >= entryPrice) {
            showToast('error', "Take profit must be below the entry price for a short position.");
            return false;
          }
          needsTpUpdate = true;
        }

        if (newSlValue) {
          const stopLoss = parseFloat(newSlValue);
          if (liquidationPrice !== null && liquidationPrice > 0) {
            if (trade.long && stopLoss <= liquidationPrice) {
              toast.error(
                `Stop Loss cannot be below liquidation price (${formatPrice(liquidationPrice, true)}).`,
                { position: "bottom-right", autoClose: 5000, hideProgressBar: false, closeOnClick: true, pauseOnHover: true, draggable: true }
              );
              return false;
            } else if (!trade.long && stopLoss >= liquidationPrice) {
              toast.error(
                `Stop Loss cannot be above liquidation price (${formatPrice(liquidationPrice, true)}). The contract will reject orders beyond the -90% PnL threshold.`,
                { position: "bottom-right", autoClose: 5000, hideProgressBar: false, closeOnClick: true, pauseOnHover: true, draggable: true }
              );
              return false;
            }
          }
          if (trade.long && stopLoss >= entryPrice) {
            showToast('error', "Stop loss must be below the entry price for a long position.");
            return false;
          } else if (!trade.long && stopLoss <= entryPrice) {
            showToast('error', "Stop loss must be above the entry price for a short position.");
            return false;
          }
          needsSlUpdate = true;
        }
      }

      // Track which operations were performed
      let tpSlUpdated = false;
      let leverageUpdated = false;

      if (isEvm) {
        // ---------------------------------------------------------------
        // EVM WALLET - multi-step flow with step-by-step blocking toast
        // ---------------------------------------------------------------
        const tradeIndexNumber = getTradeIndexNumber(trade.user_trade_index);

        // Build ordered list of steps
        const steps = [];
        if (needsTpUpdate) steps.push({ label: "Updating take profit...", fn: () => updateTakeProfitEvm(tradeIndexNumber, newTpValue, dispatch) });
        if (needsSlUpdate) steps.push({ label: "Updating stop loss...", fn: () => updateStopLossEvm(tradeIndexNumber, newSlValue, dispatch) });
        if (needsLeverageUpdate) steps.push({ label: "Updating leverage...", fn: () => updateLeverageEvm(tradeIndexNumber, newLeverageValue, dispatch) });

        if (steps.length === 0) {
          showToast('info', "No changes were made to the position");
          return false;
        }

        const totalSteps = steps.length;

        for (let i = 0; i < steps.length; i++) {
          const { label, fn } = steps[i];
          const stepNum = i + 1;

          // Show or update the blocking toast with current step
          if (i === 0) {
            showTransactionToast(label, stepNum, totalSteps);
          } else {
            updateTransactionToast(label, stepNum, totalSteps);
          }

          const result = await fn();
          if (!result.success) {
            showTxError("Failed: " + label.replace("...", ""));
            return false;
          }
        }

        // All EVM steps succeeded
        tpSlUpdated = needsTpUpdate || needsSlUpdate;
        leverageUpdated = needsLeverageUpdate;

        // Build success message
        const parts = [];
        if (tpSlUpdated) parts.push("TP/SL");
        if (leverageUpdated) parts.push("leverage");
        showTxSuccess("Position updated successfully (" + parts.join(" and ") + ")");
      } else {
        // ---------------------------------------------------------------
        // COSMOS WALLET - single-tx flows (unchanged behaviour)
        // ---------------------------------------------------------------
        if (needsLeverageUpdate) {
          const leverageMessage = {
            update_leverage: {
              trade_index: trade.user_trade_index,
              new_leverage: newLeverageValue,
            },
          };

          const leverageRes = await executePerps(
            nibiru.chainId,
            nibiru.endptTm,
            getContractAddresses().perp,
            dispatch,
            leverageMessage,
            account,
            walletType
          );

          if (!leverageRes) {
            throw new Error("Failed to update leverage");
          }
          leverageUpdated = true;
        }

        if (needsTpUpdate || needsSlUpdate) {
          const tpSlMessage = {
            update_tp: {
              trade_index: trade.user_trade_index,
              new_tp: newTpValue,
              new_sl: newSlValue,
            },
          };

          const tpSlRes = await executePerps(
            nibiru.chainId,
            nibiru.endptTm,
            getContractAddresses().perp,
            dispatch,
            tpSlMessage,
            account,
            walletType
          );

          if (!tpSlRes) {
            throw new Error("Failed to update TP/SL");
          }
          tpSlUpdated = true;
        }

        // Provide feedback based on what was updated
        if (tpSlUpdated && leverageUpdated) {
          showToast('success', "Position updated successfully (TP/SL and leverage)");
        } else if (tpSlUpdated) {
          showToast('success', "Position updated successfully (TP/SL)");
        } else if (leverageUpdated) {
          showToast('success', "Position updated successfully (leverage)");
        } else {
          showToast('info', "No changes were made to the position");
        }
      }

      return true;
    } catch (err) {
      console.error("Update position error:", err);
      dismissTransactionToast();
      showToast('error', `Failed to update position: ${err.message}`);
      return false;
    }
  };

  // Add validation timeout refs for delayed validation
  const tpValidationTimeoutRef = useRef(null);
  const slValidationTimeoutRef = useRef(null);

  // Enhanced TP validation with real-time checking
  const validateAndSetTP = (value) => {
    // Strip commas and store raw number to avoid parseFloat issues
    const cleanValue = unformatNumber(value);
    setModalInputs({ ...modalInputs, newTp: cleanValue });

    // Clear previous timeout
    if (tpValidationTimeoutRef.current) {
      clearTimeout(tpValidationTimeoutRef.current);
    }

    // Skip validation if empty
    if (!cleanValue || cleanValue === '') {
      return;
    }

    // Set new timeout for delayed validation using shared conditions
    tpValidationTimeoutRef.current = setTimeout(() => {
      validateTPConditions(cleanValue);
    }, 3000); // 3-second delay
  };

  // Enhanced SL validation with real-time checking
  const validateAndSetSL = (value) => {
    // Strip commas and store raw number to avoid parseFloat issues
    const cleanValue = unformatNumber(value);
    setModalInputs({ ...modalInputs, newSl: cleanValue });

    // Clear previous timeout
    if (slValidationTimeoutRef.current) {
      clearTimeout(slValidationTimeoutRef.current);
    }

    // Skip validation if empty
    if (!cleanValue || cleanValue === '') {
      return;
    }

    // Set new timeout for delayed validation using shared conditions
    slValidationTimeoutRef.current = setTimeout(() => {
      validateSLConditions(cleanValue);
    }, 3000); // 3-second delay
  };

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (tpValidationTimeoutRef.current) {
        clearTimeout(tpValidationTimeoutRef.current);
      }
      if (slValidationTimeoutRef.current) {
        clearTimeout(slValidationTimeoutRef.current);
      }
    };
  }, []);

  // Validate TP only when user finishes typing (on blur) - immediate validation
  const validateTPOnBlur = () => {
    // Clear the timeout since we're doing immediate validation
    if (tpValidationTimeoutRef.current) {
      clearTimeout(tpValidationTimeoutRef.current);
    }

    // Strip commas before parsing to avoid parseFloat("90,000") returning 90
    const cleanValue = String(modalInputs.newTp || "").replace(/,/g, "").trim();
    
    // If empty, just return
    if (!cleanValue || cleanValue === '') {
      return;
    }
    
    // Immediate validation on blur using shared conditions
    if (!validateTPConditions(cleanValue)) {
      setModalInputs({ ...modalInputs, newTp: '' }); // Clear invalid value
      return;
    }

    // If valid, preserve the original input string without commas
    setModalInputs({ ...modalInputs, newTp: cleanValue });
  };

  // Validate SL only when user finishes typing (on blur) - immediate validation
  const validateSLOnBlur = () => {
    // Clear the timeout since we're doing immediate validation
    if (slValidationTimeoutRef.current) {
      clearTimeout(slValidationTimeoutRef.current);
    }

    // Strip commas before parsing to avoid parseFloat("87,000") returning 87
    const cleanValue = String(modalInputs.newSl || "").replace(/,/g, "").trim();
    
    // If empty, just return
    if (!cleanValue || cleanValue === '') {
      return;
    }
    
    // Immediate validation on blur using shared conditions
    if (!validateSLConditions(cleanValue)) {
      setModalInputs({ ...modalInputs, newSl: '' }); // Clear invalid value
      return;
    }

    // If valid, preserve the original input string without commas
    setModalInputs({ ...modalInputs, newSl: cleanValue });
  };

  // Helper function to get placeholder text for TP/SL inputs
  const getTPPlaceholder = () => {
    if (!currentMarketPrice) return "Enter take profit";
    const currentPrice = parseFloat(currentMarketPrice);
    if (trade.long) {
      return `Enter Take Profit`;
    } else {
      return `Enter Take Profit`;
    }
  };

  const getSLPlaceholder = () => {
    if (!currentMarketPrice) return "Enter stop loss";
    const currentPrice = parseFloat(currentMarketPrice);
    if (trade.long) {
      return `Enter Stop Loss`;
    } else {
      return `Enter Stop Loss`;
    }
  };

  return (
    <Modal
      open={isOpen}
      center
      onClose={() => setIsOpen(false)}
      classNames={{
        modal:
          "!p-0 w-[497px] rounded-[12px] bg-white shadow-[0_18px_45px_rgba(15,23,42,0.18)] overflow-hidden max-sm:w-[95vw] max-sm:max-w-[95vw] max-sm:rounded-[12px] max-sm:mx-2",
      }}
    >
      <div className="flex flex-col overflow-hidden max-sm:flex-col">
        {/* Header */}
        <div className="px-4 pt-4 pb-4 max-sm:px-3 max-sm:pt-3 max-sm:pb-3">
          <h2 className="text-xl font-bold text-[#202020] max-sm:text-lg max-sm:text-center">
            Manage Position
          </h2>
        </div>
        {/* Divider below header */}
        <div className="w-full h-px bg-gray-200 shrink-0"></div>

        {/* Content area */}
        <div className="overflow-x-hidden px-4 pt-4 max-sm:px-3 max-sm:pt-3 max-sm:pb-2">
          <div className="w-full h-[53px] bg-[#F8F4FC] rounded-[16px] p-1 flex items-center justify-center gap-1 mb-6 mx-auto max-sm:grid max-sm:grid-cols-2 max-sm:gap-1 max-sm:mb-4 max-sm:h-auto max-sm:p-1.5">
            {tabs.map((tab) => (
              <div
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`cursor-pointer rounded-[12px] flex-1 flex items-center justify-center max-sm:w-full max-sm:py-2.5 max-sm:text-sm ${
                  activeTab === tab.id
                    ? "h-[45px] px-4 py-3 bg-white rounded-[12px] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                    : "h-[45px] px-4 py-3"
                }`}
              >
                {activeTab === tab.id ? (
                  <div className="text-black font-semibold max-sm:text-sm">{tab.label}</div>
                ) : (
                  <div className="text-[#959595] max-sm:text-sm">{tab.label}</div>
                )}
              </div>
            ))}
          </div>

          {/* Tab content */}
          <div className="max-sm:flex-1 max-sm:pb-2">
            {activeTab === "position" && (
              <div className="space-y-6 max-sm:space-y-4">
                {/* Take Profit Section */}
                <div>
                  <label className="block text-sm font-semibold mb-2 text-[#202020] max-sm:text-xs max-sm:mb-1">
                    Take Profit {trade.long ? '(Above Market Price)' : '(Below Market Price)'}
                  </label>
                  <div className="w-full">
                    <input
                      type="text"
                      value={
                        focusedField === "newTp"
                          ? modalInputs.newTp || ""
                          : formatNumberForInput(modalInputs.newTp)
                      }
                      onFocus={() => setFocusedField("newTp")}
                      onBlur={() => {
                        setFocusedField("");
                        validateTPOnBlur();
                      }}
                      onChange={(e) => validateAndSetTP(e.target.value)}
                      className="w-full h-[40px] bg-[#F8F4FCCC] border border-[#0000000D] rounded-[10px] outline-none text-base max-sm:text-base"
                      style={{ paddingTop: 10, paddingRight: 16, paddingBottom: 10, paddingLeft: 16 }}
                      placeholder={getTPPlaceholder()}
                    />
                    {/* Quick visual guides */}
                    <div className="flex flex-wrap items-center gap-[10px] mt-2 text-[11px] text-gray-600">
                      <span className="h-[28px] rounded-[8px] border border-gray-200 bg-gray-100 flex items-center px-[6px] gap-[10px]">
                        Market&nbsp;{formatPriceJSX(currentMarketPrice || 0, true)}
                      </span>
                      <span className="h-[28px] rounded-[8px] border border-gray-200 bg-gray-100 flex items-center px-[6px] gap-[10px]">
                        Entry&nbsp;{formatPriceJSX(trade.open_price || 0, true)}
                      </span>
                    </div>
                    {(() => {
                      const guide = getTpGuidance();
                      if (!guide) return null;
                      return (
                        <div className="flex flex-wrap items-center gap-[10px] mt-2 text-[11px]">
                          <span className="h-[28px] rounded-[8px] border border-green-200 bg-green-50 text-green-700 flex items-center px-[6px] gap-[10px]">
                            {trade.long
                              ? <>Min TP&nbsp;{formatPriceJSX(guide.value, true)}</>
                              : <>Max TP&nbsp;{formatPriceJSX(guide.value, true)}</>}
                          </span>
                          <span className="text-gray-500">
                            Choose a value on the corret side of this guide
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Stop Loss Section */}
                <div>
                  <label className="block text-sm font-semibold mb-2 text-[#202020] max-sm:text-xs max-sm:mb-1">
                    Stop Loss {trade.long ? '(Below Market Price)' : '(Above Market Price)'}
                  </label>
                  <div className="w-full">
                    <input
                      type="text"
                      value={
                        focusedField === "newSl"
                          ? modalInputs.newSl || ""
                          : formatNumberForInput(modalInputs.newSl)
                      }
                      onFocus={() => setFocusedField("newSl")}
                      onBlur={() => {
                        setFocusedField("");
                        validateSLOnBlur();
                      }}
                      onChange={(e) => validateAndSetSL(e.target.value)}
                      className="w-full h-[40px] bg-[#F8F4FCCC] border border-[#0000000D] rounded-[10px] outline-none text-base max-sm:text-base"
                      style={{ paddingTop: 10, paddingRight: 16, paddingBottom: 10, paddingLeft: 16 }}
                      placeholder={getSLPlaceholder()}
                    />
                    {/* Quick visual guides */}
                    <div className="flex flex-wrap items-center gap-[10px] mt-2 text-[11px] text-gray-600">
                      <span className="h-[28px] rounded-[8px] border border-gray-200 bg-gray-100 flex items-center px-[6px] gap-[10px]">
                        Market&nbsp;{formatPriceJSX(currentMarketPrice || 0, true)}
                      </span>
                      <span className="h-[28px] rounded-[8px] border border-gray-200 bg-gray-100 flex items-center px-[6px] gap-[10px]">
                        Entry&nbsp;{formatPriceJSX(trade.open_price || 0, true)}
                      </span>
                      {liquidationPrice !== null && liquidationPrice > 0 && (
                        <span className="h-[28px] rounded-[8px] border border-gray-200 bg-gray-100 flex items-center px-[6px] gap-[10px]">
                          Liq&nbsp;{formatPriceJSX(liquidationPrice, true)}
                        </span>
                      )}
                    </div>
                    {(() => {
                      const guide = getSlGuidance();
                      if (!guide) return null;
                      const { lower, upper, hasLiq } = guide;
                      return (
                        <div className="flex flex-wrap items-center gap-[10px] mt-2 text-[11px]">
                          {hasLiq && lower != null && upper != null ? (
                            <span className="h-[28px] rounded-[8px] border border-amber-200 bg-amber-50 text-amber-700 flex items-center px-[6px] gap-[10px]">
                              SL range&nbsp;{formatPriceJSX(lower, true)}&nbsp;-&nbsp;{formatPriceJSX(upper, true)}
                            </span>
                          ) : trade.long && upper != null ? (
                            <span className="h-[28px] rounded-[8px] border border-amber-200 bg-amber-50 text-amber-700 flex items-center px-[6px] gap-[10px]">
                              SL below&nbsp;{formatPriceJSX(upper, true)}
                            </span>
                          ) : !trade.long && lower != null ? (
                            <span className="h-[28px] rounded-[8px] border border-amber-200 bg-amber-50 text-amber-700 flex items-center px-[6px] gap-[10px]">
                              SL above&nbsp;{formatPriceJSX(lower, true)}
                            </span>
                          ) : null}
                          <span className="text-gray-500">
                            Pick a value inside this suggested zone
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Leverage Section */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-[#202020] max-sm:text-xs max-sm:mb-1">
                    Leverage
                  </label>
                  <div className="w-full">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[1-9][0-9]*"
                      value={modalInputs.leverage || ""}
                      onFocus={() => setFocusedField("leverage")}
                      onBlur={() => {
                        setFocusedField("");
                        const parsed = parseInt(modalInputs.leverage, 10);
                        if (!isNaN(parsed) && parsed >= 1 && parsed <= 100) {
                          setModalInputs({ ...modalInputs, leverage: parsed.toString() });
                        } else if (modalInputs.leverage) {
                          setModalInputs({ ...modalInputs, leverage: '' });
                        }
                      }}
                      onChange={(e) => handleLeverageChange(e.target.value)}
                      onKeyPress={(e) => {
                        if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                          e.preventDefault();
                        }
                      }}
                      className="w-full h-12 bg-[#F8F4FCCC] px-4 py-3 rounded-xl border-none outline-none text-base max-sm:px-2 max-sm:py-3 max-sm:text-base max-sm:rounded-xl"
                      placeholder="Enter leverage"
                      min="1"
                      max="100"
                    />
                    <div className="text-xs text-gray-500 mt-1 max-sm:text-xs">
                      Current : {trade.leverage}x
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Limit Order Tab Content */}
            {activeTab === "limit" && trade.trade_type === "limit" && (
              <div className="space-y-6 max-sm:space-y-4">
                <div>
                  <h3 className="font-semibold mb-2 text-[#202020] max-sm:text-sm max-sm:mb-1">
                    Update Limit Order
                  </h3>
                  <div className="grid grid-cols-2 gap-2 mt-2 max-sm:grid-cols-1 max-sm:gap-3 max-sm:mt-1">
                    <div className="w-full">
                      <div className="text-sm font-medium mb-1 max-sm:text-xs">Price</div>
                      <input
                        type="text"
                        value={modalInputs.price || ""}
                        onChange={(e) =>
                          setModalInputs({ ...modalInputs, price: unformatNumber(e.target.value) })
                        }
                        onBlur={() =>
                          setModalInputs({ ...modalInputs, price: formatNumberForInput(modalInputs.price) })
                        }
                        placeholder="New Price"
                        className="w-full h-12 bg-[#F8F4FCCC] px-4 py-3 rounded-xl border-none outline-none text-base max-sm:px-2 max-sm:py-3 max-sm:text-base max-sm:rounded-xl"
                      />
                    </div>
                    <div className="w-full">
                      <div className="text-sm font-medium mb-1 max-sm:text-xs">Slippage</div>
                      <input
                        type="text"
                        step={0.1}
                        max={1}
                        min={0.1}
                        value={modalInputs.slippage_p || ""}
                        onChange={(e) =>
                          setModalInputs({
                            ...modalInputs,
                            slippage_p: unformatNumber(e.target.value),
                          })
                        }
                        onBlur={() =>
                          setModalInputs({
                            ...modalInputs,
                            slippage_p: formatNumber(modalInputs.slippage_p),
                          })
                        }
                        placeholder="Slippage percent"
                        className="w-full h-12 bg-[#F8F4FCCC] px-4 py-3 rounded-xl border-none outline-none text-base max-sm:px-2 max-sm:py-3 max-sm:text-base max-sm:rounded-xl"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-4 max-sm:grid-cols-1 max-sm:gap-3 max-sm:mt-3">
                    <div className="w-full">
                      <div className="text-sm font-medium mb-1 max-sm:text-xs">TP {trade.long ? '(Above Market)' : '(Below Market)'}</div>
                      <input
                        type="text"
                        value={modalInputs.newTp || ""}
                        onChange={(e) => validateAndSetTP(e.target.value)}
                        onBlur={() => validateTPOnBlur()}
                        placeholder={getTPPlaceholder()}
                        className="w-full h-12 bg-[#F8F4FCCC] px-4 py-3 rounded-xl border-none outline-none text-base max-sm:px-2 max-sm:py-3 max-sm:text-base max-sm:rounded-xl"
                      />
                    </div>
                    <div className="w-full">
                      <div className="text-sm font-medium mb-1 max-sm:text-xs">SL {trade.long ? '(Below Market)' : '(Above Market)'}</div>
                      <input
                        type="text"
                        value={modalInputs.newSl || ""}
                        onChange={(e) => validateAndSetSL(e.target.value)}
                        onBlur={() => validateSLOnBlur()}
                        placeholder={getSLPlaceholder()}
                        className="w-full h-12 bg-[#F8F4FCCC] px-4 py-3 rounded-xl border-none outline-none text-base max-sm:px-2 max-sm:py-3 max-sm:text-base max-sm:rounded-xl"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "limit" && trade.trade_type !== "limit" && (
              <div className="text-lg max-sm:text-base max-sm:text-center max-sm:py-8">Trade type is not limit order</div>
            )}

            {/* Actions Tab Content */}
            {activeTab === "actions" && (
              <div className="space-y-6 max-sm:space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-[#202020] max-sm:text-xs max-sm:mb-1">
                    Actions Manage Position
                  </label>
                  <div className="grid grid-cols-2 gap-2 max-sm:grid-cols-1 max-sm:gap-3">
                    {[
                      { type: PendingOrderType.LIMIT_OPEN, label: "Limit Open" },
                      { type: PendingOrderType.STOP_OPEN, label: "Stop Open" },
                      { type: PendingOrderType.TP_CLOSE, label: "Tp Close" },
                      { type: PendingOrderType.SL_CLOSE, label: "Sl Close" },
                      { type: PendingOrderType.LIQ_CLOSE, label: "Liq Close" },
                    ].map(({ type, label }) =>
                      modalInputs.orderType === type ? (
                        <PurpleButton
                          key={type}
                          text={label}
                          onClick={() =>
                            setModalInputs({ ...modalInputs, orderType: type })
                          }
                          className="text-sm font-medium max-sm:text-base"
                        />
                      ) : (
                        <div
                          key={type}
                          onClick={() =>
                            setModalInputs({ ...modalInputs, orderType: type })
                          }
                          className="select-none px-[14px] py-[8px] rounded-[12px] bg-white border border-gray-200 text-[#202020] text-center text-sm font-medium cursor-pointer max-sm:text-base"
                        >
                          {label}
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom button area */}
        <div className="px-4 pt-6 pb-3 shrink-0 max-sm:px-3 max-sm:pt-4 max-sm:pb-2 max-sm:sticky max-sm:bottom-0 max-sm:bg-white">
          {activeTab === "position" && (
            <PurpleButton
              text={"Update Position"}
              onClick={() => handleUpdatePosition()}
              className="w-full h-10 max-sm:py-3 max-sm:text-base max-sm:font-semibold"
            />
          )}
          {activeTab === "limit" && trade.trade_type === "limit" && (
            <div className="grid grid-cols-2 gap-2 max-sm:grid-cols-1 max-sm:gap-3">
              <PurpleButton
                text={"Update Limit"}
                onClick={() => handleUpdateLimitOrder()}
                className="w-full h-10 max-sm:py-3 max-sm:text-base"
              />
              <RedButton
                text={"Cancel Limit"}
                onClick={() => handleCancelLimitOrder()}
                className="w-full h-10 max-sm:py-3 max-sm:text-base"
              />
            </div>
          )}
          {activeTab === "actions" && (
            <div className="space-y-2 max-sm:space-y-2">
              <PurpleButton
                text={"Trigger Trade"}
                onClick={() => handleTriggerTrade()}
                className="w-full h-10 max-sm:py-3 max-sm:text-base"
              />
              <RedButton
                text={"Close Trade"}
                onClick={() => handleCloseTrade()}
                className="w-full h-10 max-sm:py-3 max-sm:text-base"
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default UpdateActionsModal;